<template>
    <div class="cell order-summary" :dusk="'order-summary-' + order.id">
        <div
            :class="['align-middle', 'grid-x', 'align-justify', 'order-summary-header', { 'is-processing': isProcessing }]">
            <div class="cell">
                <p class="display-small order-label margin-0"><span class="bold" dusk="order-summary-id">Order #{{
                        order.id }} • </span> {{ scheduledTime }}</p>
                <p class="display-4 bold order-status margin-0" v-if="isProcessing" dusk="order-summary-status">{{
                    orderStatus }}</p>
                <div :class="['flex-container', 'status-container', statusProgress]"
                    v-if="isProcessing && order.status !== OrderStatus.CANCEL">
                    <div class="status-bar-container cell auto">
                        <span class="status-bar first"><span class="progress-bar"></span></span>
                        <p :class="['display-small', 'status-label', 'margin-0', 'active']">Received</p>
                    </div>
                    <div class="status-bar-container cell auto">
                        <span class="status-bar second"><span class="progress-bar"></span></span>
                        <p
                            :class="['display-small', 'status-label', 'margin-0', { active: order.status === OrderStatus.IN_PROGRESS || order.status === OrderStatus.DONE }]">
                            Preparing</p>
                    </div>
                    <div class="status-bar-container cell auto">
                        <span class="status-bar last"><span class="progress-bar"></span></span>
                        <p
                            :class="['display-small', 'status-label', 'margin-0', { active: order.status === OrderStatus.DONE }]">
                            {{ (order.is_picked_up) ? 'Pick-up ready' : 'Delivery' }}</p>
                    </div>
                </div>
            </div>
        </div>
        <div class="grid-x grid-padding-x order-summary-body">
            <div class="cell auto order-summary-products">
                <div class="grid-x">
                    <order-item v-for="product in order.products" :key="product.id" :product="product">
                    </order-item>
                </div>
            </div>
            <div class="cell large-3 order-summary-total">
                <div class="grid-x align-justify">
                    <div class="cell shrink">
                        <p class="display-small color-battleship-gray margin-0" v-if="subtotal">Subtotal</p>
                        <p class="display-small color-battleship-gray margin-0" v-if="deliveryFee">Delivery Fee</p>
                        <p class="display-small color-battleship-gray margin-0" v-if="tip">Tip</p>
                        <p class="display-large margin-0">Total</p>
                    </div>
                    <div class="cell auto">
                        <p class="text-right display-small color-battleship-gray margin-0" v-if="subtotal">{{ subtotal
                            }}</p>
                        <p class="text-right display-small color-battleship-gray margin-0" v-if="deliveryFee">{{
                            deliveryFee }}</p>
                        <p class="text-right display-small color-battleship-gray margin-0" v-if="tip">{{ tip }}</p>
                        <p class="text-right display-large margin-0">{{ total }}</p>
                    </div>
                    <div class="text-center cell order-btn-container">
                        <button class="button full-width margin-0" :disabled="!canReorder || isDisabled"
                            @click="reorder" dusk="order-summary-reorder-btn">{{ (cartMaxProducts > 0 && cartFull) ?
                            'Basket full' : 'Reorder' }}</button>
                    </div>
                </div>
            </div>
            <p v-if="productsHaveBeenUpdated" style="padding: 20px 0px 0px 20px;"
                class="display-small color-battleship-gray margin-0"><i>Product prices may have changed since your last
                    order and could be updated during checkout.</i></p>
        </div>
        <div class="warning-box grid-x" v-if="hasProductsOutOfStock || hasIngredientsOutOfStock">
            <span class="warning-icon cell shrink" v-html="Icon.ERROR"></span>
            <div class="cell auto">
                <p class="display-small margin-0" v-if="hasProductsOutOfStock">{{ outOfStockProductsText }}</p>
                <p class="display-small margin-0"
                    v-for="(outOfStockProductIngredients, i) in outOfStockIngredientsByProduct" :key="i">
                    {{ outOfStockProductIngredients.product }} can't be re-order as {{
                        outOfStockProductIngredients.ingredients.join(', ') }}
                    {{ (outOfStockProductIngredients.ingredients.length > 1) ? 'are' : 'is' }} out of stock. Please
                    re-order it via the menu to select alternative
                    ingredient{{ (outOfStockProductIngredients.ingredients.length > 1) ? 's' : '' }}.
                </p>
            </div>
        </div>
        <div class="warning-box grid-x" v-if="orderProductsQuantity > cartSpace && !cartFull">
            <span class="warning-icon cell shrink" v-html="Icon.ERROR"></span>
            <div class="cell auto">
                <p class="display-small margin-0">This order won't fit in your basket ({{ cartMaxProducts }} products
                    max)</p>
            </div>
        </div>

        <div v-if="unavailableProducts.length > 0" class="warning-box grid-x">
            <span class="warning-icon cell shrink" v-html="Icon.ERROR"></span>
            <div class="cell auto">
                <div v-for="unavailableProduct in unavailableProducts" :key="unavailableProduct.id">
                    <p class="display-small margin-0">{{ unavailableProduct.name }} is not available at this time and
                        won't be added to your cart.</p>
                    <p class="display-small margin-0">{{ scheduleText(unavailableProduct.schedule) }}</p>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import Product from '@/models/product';
import OrderItem from '@/components/front/user/OrderItem.vue';
import Ingredient from '@/models/ingredient';
import Order, { OrderStatus } from '@/models/order';
import { Icon } from '@/front/icons';
import { triggerGlobalEvent } from '@/utils/global-event';
import TimeSchedulerModule from '@/stores/modules/time-scheduler';
import CartModule from '@/stores/modules/cart';
import FoodModule from '@/stores/modules/food';
import { numberToCurrency } from '@/utils/utilities';
import { isProductUnavailable, scheduleTextForToday } from '@/utils/availability';
import Settings, { SettingNamespace } from '@/utils/settings';

@Component({
    components: { OrderItem }
})
export default class OrderSummary extends Vue
{
    @Prop(Object) readonly order!: Order;
    @Prop(Boolean) readonly isDisabled!: boolean;

    foodModule!: FoodModule;
    cartModule!: CartModule;
    timeSchedulerModule!: TimeSchedulerModule;
    unavailabilityTimeStart: string | null = null;
    unavailabilityTimeEnd: string | null = null;
    timer: number = Date.now();
    interval: any = null;
    statusProgress: string = 'confirmed';
    timeSlotSize: number | null = null;

    async created() {
        this.cartModule = getModule(CartModule, this.$store);
        this.timeSchedulerModule = getModule(TimeSchedulerModule, this.$store);
        this.foodModule = getModule(FoodModule, this.$store);
        const settings = await Settings.getInstance();
        this.timeSlotSize = settings.get(SettingNamespace.ORDERS, 'time_slot_size')?.value ?? null;
    }

    beforeDestroy() {
        clearInterval(this.interval);
        this.interval = null;
    }

    mounted() {
        this.interval = setInterval(() => this.timer = Date.now(), 20000);
    }

    async reorder() {
        for (let i in this.order.products) {
            let product = this.order.products[i];
            // Convert Product instance to plain object to preserve all nested properties
            // This ensures ingredients and options are properly cloned
            let cloneProduct: any = {
                id: product.id,
                name: product.name,
                position: product.position,
                slug: product.slug,
                description: product.description,
                is_preset: product.is_preset,
                price: product.origin ? product.origin.price : product.price,
                instructions: product.instructions,
                is_customisable: product.is_customisable,
                is_active: product.is_active,
                schedule: product.schedule,
                availability: product.availability,
                in_stock: product.in_stock,
                quantity: product.quantity,
                // Ensure all ingredients are marked as selected and have prices
                ingredients: product.ingredients.map((ing: any) => {
                    // Get price - could be from ing.price (Ingredient instance) or from the raw data
                    let ingredientPrice = 0;
                    if (ing.price !== undefined && ing.price !== null) {
                        ingredientPrice = Number(ing.price);
                    } else if (typeof ing === 'object' && 'price' in ing) {
                        ingredientPrice = Number(ing.price) || 0;
                    }

                    return {
                        id: ing.id,
                        name: ing.name,
                        price: ingredientPrice,
                        // Preserve quantity, defaulting to 1 if not set
                        quantity: ing.quantity !== undefined && ing.quantity !== null ? Number(ing.quantity) : 1,
                        quantifiable: ing.quantifiable,
                        is_required: ing.is_required,
                        in_stock: ing.in_stock,
                        // Mark as selected since they're attached to the product
                        selected: true,
                        step_id: ing.step_id,
                    };
                }),
                options: product.options.map((opt: any) => ({
                    id: opt.id,
                    name: opt.name,
                    label: opt.label,
                    type: opt.type,
                    value: opt.value,
                    price: opt.price !== undefined && opt.price !== null ? Number(opt.price) : 0,
                    accepted_values: opt.accepted_values,
                })),
                type: product.type,
                origin: product.origin,
                image: product.image,
                updated_at: product.updated_at,
            };

            // Handle unavailability action option
            for (let index in cloneProduct.options) {
                const option = cloneProduct.options[index];
                if (option.name === 'Unavailability action' && option.value !== 'substitute') {
                    option.value = 'substitute';
                    cloneProduct.id = new Date().getTime();
                }
            }

            if (this.isProductInStock(product) && !isProductUnavailable(product, this.timeSchedulerModule.selectedTimeSlot) && !this.productHasIngredientsOutOfStock(product)) {
                const cartProduct = new Product(cloneProduct);
                this.cartModule.addProduct(cartProduct);
                triggerGlobalEvent('triggerNavigationDisplay');
            }
        }
    }

    get isProcessing() {
        const orderScheduledTime = new Date(this.order.scheduled_time);
        let orderProcessingTime = orderScheduledTime.setHours(this.order.scheduled_time.getHours() + 3);
        return this.timer < orderProcessingTime;
    }

    get OrderStatus() {
        return OrderStatus;
    }

    get orderStatus() {
        switch (this.order.status) {
            case OrderStatus.DONE:
                const orderScheduledTime = new Date(this.order.scheduled_time);
                let orderProcessingTime = orderScheduledTime.setHours(this.order.scheduled_time.getHours() + 3);
                this.statusProgress = 'done';
                if (this.timer >= orderProcessingTime) {
                    if (!this.order.is_picked_up) {
                        return 'Delivered. Enjoy your food!'
                    }
                }
                if (!this.order.is_picked_up) {
                    this.statusProgress = 'on_the_way';
                    return 'Your food is arriving soon!';
                }
                return 'Your food is ready for pick up!';
            case OrderStatus.CONFIRMED:
                this.statusProgress = 'confirmed';
                return 'We received your order.';
            case OrderStatus.IN_PROGRESS:
                this.statusProgress = 'in_progress';
                return 'Your food is being prepared.';
            case OrderStatus.CANCEL:
                return 'Your order has been cancelled.';
            default:
                return;
        }
    }

    get cartMaxProducts() {
        return this.cartModule.maxOrderProducts;
    }

    get cartSpace() {
        return this.cartModule.maxOrderProducts - this.cartModule.productsQuantity;
    }

    get cartFull() {
        return this.cartModule.maxProductsReached;
    }

    get scheduledTime() {
        return this.order.scheduled_time.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }).replace(',', ' at');
    }

    get total() {
        return numberToCurrency(this.order.total);
    }

    get subtotal() {
        return numberToCurrency(this.order.total - (this.order.delivery_fee ?? 0) - (this.order.tip_amount ?? 0));
    }

    get isDelivery() {
        return !this.order.is_picked_up;
    }

    get tip() {
        return (this.order.tip_amount) ? numberToCurrency(this.order.tip_amount) : null;
    }

    get deliveryFee() {
        return (this.order.delivery_fee) ? numberToCurrency(this.order.delivery_fee) : null;
    }

    get orderProductsQuantity() {
        return this.order.products
            .map((product: Product) => product.quantity)
            .reduce((prevQty: number, currentQty: number | undefined) => prevQty + ((currentQty !== undefined) ? currentQty : 1), 0);
    }

    get unavailableProducts(): Product[] {
        return this.order.products.filter((product: Product) => isProductUnavailable(product, this.timeSchedulerModule.selectedTimeSlot));
    }

    get outOfStockProductsText() {
        return `${this.outOfStockProducts.map((product: Product) => product.name).join(', ')} ${(this.outOfStockProducts.length > 1) ? 'are' : 'is'} out of stock and won't be added to your basket if you re-order.`;
    }

    get outOfStockIngredientsByProduct() {
        let outOfStockIngredientsByProduct: { product: string, ingredients: string[] }[] = [];
        this.order.products.forEach((product: Product) => {
            let ingredientsOutOfStock: string[] = product.ingredients
                .filter((ingredient: Ingredient) => !ingredient.is_required && !ingredient.in_stock)
                .map((ingredient: Ingredient) => ingredient.name);
            if (ingredientsOutOfStock.length > 0) {
                const outOfStockProductIndex = outOfStockIngredientsByProduct.findIndex((ingredientProduct) => ingredientProduct.product === product.name);
                if (outOfStockProductIndex === -1) {
                    outOfStockIngredientsByProduct.push({ product: product.name, ingredients: ingredientsOutOfStock });
                } else {
                    outOfStockIngredientsByProduct.splice(outOfStockProductIndex, 1, { product: product.name, ingredients: ingredientsOutOfStock });
                }
            }
        });
        return outOfStockIngredientsByProduct;
    }

    get productsHaveBeenUpdated() {
        const orderScheduledTime = new Date(this.order.scheduled_time).getTime();
        for (let product of this.order.products) {
            if (product.updated_at) {
                const productUpdatedAt = new Date(product.updated_at).getTime();
                if (productUpdatedAt > orderScheduledTime) {
                    return true;
                }
            }
            for (let ingredient of product.ingredients) {
                if (ingredient.updated_at) {
                    const ingredientUpdatedAt = new Date(ingredient.updated_at).getTime();
                    if (ingredientUpdatedAt > orderScheduledTime) {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    get outOfStockProducts() {
        const outOfStockProducts = this.order.products.filter((product: Product) => {
            if (!product.is_preset) {
                return product.origin?.in_stock === false;
            }
            return product.in_stock === false;
        });
        return outOfStockProducts as Product[];
    }

    get hasProductsOutOfStock() {
        return this.outOfStockProducts.length > 0;
    }

    get hasIngredientsOutOfStock() {
        return this.outOfStockIngredientsByProduct.length > 0;
    }

    get productHasIngredientsOutOfStock() {
        return (product: Product) => this.outOfStockIngredientsByProduct.find((outOfStockProduct) => outOfStockProduct.product === product.name);
    }

    get isProductInStock() {
        return (product: Product) => (!product.is_preset) ? product.origin?.in_stock : product.in_stock;
    }

    get hasOnlyUnavailableProducts() {
        let unavailableProducts: number = 0;
        this.order.products.forEach((product: Product) => {
            if (isProductUnavailable(product, this.timeSchedulerModule.selectedTimeSlot) || !this.isProductInStock(product) || this.productHasIngredientsOutOfStock(product)) {
                unavailableProducts++;
            }
        });
        return unavailableProducts === this.order.products.length;
    }

    get hasLegacyIngredients() {
        // Check if any product has ingredients without step_id (legacy orders)
        return this.order.products.some((product: Product) => 
            product.ingredients.some((ing: Ingredient) => ing.step_id === undefined || ing.step_id === null)
        );
    }

    get canReorder() {
        return !this.cartFull && this.orderProductsQuantity <= this.cartSpace && !this.hasOnlyUnavailableProducts && !this.hasLegacyIngredients;
    }

    get Icon() {
        return Icon;
    }

    get scheduleText() {
        return (schedules: any) => scheduleTextForToday(schedules, this.timeSlotSize, this.timeSchedulerModule.selectedTimeSlot);
    }
}
</script>
<style lang="scss" scoped>
@import '~foundation-sites/scss/foundation';
@import '~scss/front/settings';

ul {
    list-style-type: none;
    margin-left: 0;
}

.status-container {
    &.confirmed .first .progress-bar {
        width: 50% !important;
    }

    &.in_progress {
        .first .progress-bar {
            width: 100% !important;
        }

        .second .progress-bar {
            width: 50% !important;
        }
    }

    &.on_the_way {

        .first .progress-bar,
        .second .progress-bar {
            width: 100% !important;
        }

        .last .progress-bar {
            width: 50% !important;
        }
    }

    &.done {

        .first .progress-bar,
        .second .progress-bar,
        .last .progress-bar {
            width: 100% !important;
        }
    }

    .status-bar-container {

        &:not(:last-child) {
            margin-right: 5px;
        }

        .status-bar {
            height: 7px;
            display: inline-block;
            background: rgba(white, 0.1);
            border-radius: 23px;
            width: 100%;

            &:not(.last) {
                margin-right: 5px;
            }

            .progress-bar {
                display: flex;
                border-radius: 23px;
                width: 0%;
                height: 100%;
                background-color: white;
            }
        }

        .status-label {
            margin-top: 5px;
            line-height: 0;
            color: rgba(white, 0.8);

            &.active {
                color: white;
            }
        }
    }
}

.basket-size-warning {
    padding-top: 10px;
}

.order-summary {
    background: $white;
    margin-top: 25px;
    box-shadow: 0px 6px 18px rgba(0, 0, 0, 0.1202);
    border-radius: 12px;

    .order-summary-header {
        padding: 20px 0 10px 20px;

        .order-label {
            color: var(--order-card-header-label-color);
        }

        .order-status {
            color: var(--order-card-header-status-color);
            line-height: 27px;
            padding-top: 10px;
            padding-bottom: 7px;
        }

        &.is-processing {
            background: var(--order-card-header-bg);
            padding: 20px;
            border-radius: 12px 12px 0px 0px;
            margin-bottom: 20px;

            .order-label {
                color: rgba(white, 0.8);
            }
        }
    }

    .order-summary-body {
        padding-bottom: 20px;

    }

    .order-summary-products {
        @include breakpoint (medium) {
            padding-left: 20px;
            border-right: 1px solid var(--light-gray);
        }

        @include breakpoint (small only) {
            border-bottom: 1px solid var(--light-gray);
            margin-bottom: 10px;
            padding-left: 20px;
            padding-right: 20px;
        }
    }

    .order-summary-total {
        @include breakpoint (small only) {
            padding-left: 20px;
            padding-right: 20px;
        }
    }

    .order-btn-container {
        margin-top: 15px;
    }

}
</style>
